/**
 * Decision 0144 (experiment 11): ref chips where the page holds no project snapshot (Home, the bell), and deciding a
 * card where you read it. `GET …/projects/:projectId/refs?r=` answers each ref's view (core `refView`) for the
 * signed-in person; `POST …/cards/:proposalId/decide` sends the same DecideProposal as Needs you.
 */
import {
  type CardDecideBody,
  CardDecideResult,
  type CardShape,
  type ProposalDecision,
  REF_LOOKUP_MAX,
  RefLookup,
  type RefView,
} from "@minga/protocol"
import { ApiError, apiJson } from "./api"

const projectPath = (workspaceId: string, projectId: string) =>
  `/api/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}`

/** The views of these refs in one project (at most REF_LOOKUP_MAX, the first ones). */
export const fetchRefViews = async (
  workspaceId: string,
  projectId: string,
  refs: ReadonlyArray<string>,
): Promise<ReadonlyArray<RefView>> => {
  const list = [...new Set(refs)].slice(0, REF_LOOKUP_MAX)
  if (list.length === 0) return []
  const out = await apiJson(
    `${projectPath(workspaceId, projectId)}/refs?r=${encodeURIComponent(list.join(","))}`,
    RefLookup,
  )
  return out.refs
}

/** React Query options for a lookup (keyed by the sorted refs; fresh for a few seconds, like the server's cache). */
export const refViewsQuery = (workspaceId: string, projectId: string, refs: ReadonlyArray<string>) => {
  const list = [...new Set(refs)].sort()
  return {
    queryKey: ["workspaces", workspaceId, "projects", projectId, "refs", list.join(",")] as const,
    queryFn: () => fetchRefViews(workspaceId, projectId, list),
    staleTime: 5_000,
    enabled: list.length > 0,
  }
}

/** How long a looked-up view is kept; a ref the project doesn't have (an old run) is kept longer: it won't appear. */
export const REF_LOOKUP_FRESH_MS = 30_000
export const REF_LOOKUP_ABSENT_MS = 5 * 60_000
/** Chips that mount within this long of each other share one request (decision 0151). */
export const REF_LOOKUP_BATCH_MS = 16

const single = new Map<
  string,
  { readonly at: number; readonly view: Promise<RefView | null>; found: boolean | null }
>()
interface Batch {
  readonly workspaceId: string
  readonly projectId: string
  /** Each ref's answer: its view, null when the project doesn't have it, or `failed` when the request failed. */
  readonly refs: Map<string, (v: RefView | null, failed?: boolean) => void>
}
const queued = new Map<string, Batch>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

const flushLookups = (fetchViews: typeof fetchRefViews) => {
  flushTimer = null
  const batches = [...queued.values()]
  queued.clear()
  for (const batch of batches) {
    const refs = [...batch.refs.keys()]
    for (let i = 0; i < refs.length; i += REF_LOOKUP_MAX) {
      const part = refs.slice(i, i + REF_LOOKUP_MAX)
      void fetchViews(batch.workspaceId, batch.projectId, part).then(
        (views) => {
          const byRef = new Map(views.map((v) => [v.ref, v]))
          for (const ref of part) batch.refs.get(ref)?.(byRef.get(ref) ?? null)
        },
        () => {
          for (const ref of part) batch.refs.get(ref)?.(null, true)
        },
      )
    }
  }
}

/**
 * One ref's view outside React Query (a chip whose page resolver doesn't know it: a decision comment's D-n, an old run).
 * Decision 0151: the chips that mount together (a chat or a page of them) share one request per project, at most
 * REF_LOOKUP_MAX refs each, instead of one request per chip (a long chat sent about 80). A view is kept for 30 seconds;
 * a ref the project doesn't have for 5 minutes. A failed request is not kept.
 */
export const lookupOneRef = (
  workspaceId: string,
  projectId: string,
  ref: string,
  fetchViews: typeof fetchRefViews = fetchRefViews,
): Promise<RefView | null> => {
  const key = `${workspaceId}/${projectId}/${ref}`
  const hit = single.get(key)
  const age = hit ? Date.now() - hit.at : Number.POSITIVE_INFINITY
  if (hit && (age < REF_LOOKUP_FRESH_MS || (hit.found === false && age < REF_LOOKUP_ABSENT_MS)))
    return hit.view
  const project = `${workspaceId}/${projectId}`
  const batch: Batch = queued.get(project) ?? { workspaceId, projectId, refs: new Map() }
  queued.set(project, batch)
  const entry: { at: number; view: Promise<RefView | null>; found: boolean | null } = {
    at: Date.now(),
    view: Promise.resolve(null),
    found: null,
  }
  entry.view = new Promise<RefView | null>((resolve) => {
    batch.refs.set(ref, (v, failed) => {
      if (failed && single.get(key) === entry) single.delete(key)
      entry.found = v !== null
      resolve(v)
    })
  })
  single.set(key, entry)
  flushTimer ??= setTimeout(() => flushLookups(fetchViews), REF_LOOKUP_BATCH_MS)
  return entry.view
}

/** Tests: forget the single-ref cache and anything queued. */
export const resetRefLookupCache = () => {
  single.clear()
  queued.clear()
  if (flushTimer) clearTimeout(flushTimer)
  flushTimer = null
}

/** The bell's answer button (decision 0144): the card's recommended answer, re-checked by the server. */
export const answerRecommendedHttp = async (
  workspaceId: string,
  projectId: string,
  proposalId: string,
  revision: number,
): Promise<void> => {
  try {
    const out = await apiJson(
      `${projectPath(workspaceId, projectId)}/cards/${encodeURIComponent(proposalId)}/recommended`,
      CardDecideResult,
      { method: "POST", body: { revision } },
    )
    if (!out.ok) throw new Error(out.error ?? "That didn't go through.")
  } catch (error) {
    if (error instanceof ApiError && error.body && typeof error.body.error === "string")
      throw new Error(error.body.error)
    throw error
  }
}

/**
 * Decide a card from Home, the brief or the bell (the card as it was shown: its revision and content hash). Resolves
 * to the card's new shape, or rejects with the reason in words.
 */
export const decideCardHttp = async (
  workspaceId: string,
  projectId: string,
  card: Pick<CardShape, "proposalId" | "revision" | "contentHash">,
  decision: ProposalDecision,
): Promise<CardShape | null> => {
  const body: CardDecideBody = { revision: card.revision, contentHash: card.contentHash, decision }
  try {
    const out = await apiJson(
      `${projectPath(workspaceId, projectId)}/cards/${encodeURIComponent(card.proposalId)}/decide`,
      CardDecideResult,
      { method: "POST", body },
    )
    if (!out.ok) throw new Error(out.error ?? "That didn't go through.")
    return out.card ?? null
  } catch (error) {
    if (error instanceof ApiError && error.body && typeof error.body.error === "string")
      throw new Error(error.body.error)
    throw error
  }
}
